import type { Course, MenuEducation } from "./Menu.types";

/** User progress row from Firestore (may be partial in tests or during load). */
export type UserEducationProgress = { ID: string } & Record<string, unknown>;

/**
 * Filters educations into categories based on PublishedStatus and progress
 */
export const categorizeEducations = (
  educations: MenuEducation[],
  getProgress: (education: MenuEducation) => number,
): {
  ongoing: MenuEducation[];
  expiredOngoing: MenuEducation[];
  expiredCompleted: MenuEducation[];
  completed: MenuEducation[];
} => {
  const published = educations.filter((education) => {
    if (
      education.PublishedStatus === undefined ||
      education.PublishedStatus === null
    ) {
      return true;
    }
    return (
      education.PublishedStatus === "published" ||
      education.PublishedStatus === "expired"
    );
  });

  const ongoing = published.filter(
    (education) =>
      education.PublishedStatus !== "expired" &&
      getProgress(education) < 100
  );

  const expiredOngoing = published.filter(
    (education) =>
      education.PublishedStatus === "expired" &&
      getProgress(education) < 100
  );

  const expiredCompleted = published.filter(
    (education) =>
      education.PublishedStatus === "expired" &&
      getProgress(education) === 100
  );

  const completed = published.filter(
    (education) =>
      education.PublishedStatus !== "expired" &&
      getProgress(education) === 100
  );

  return {
    ongoing,
    expiredOngoing,
    expiredCompleted,
    completed,
  };
};

export const matchCoursesToEducations = (
  objects: readonly UserEducationProgress[],
  availableCourses: Course[],
): MenuEducation[] => {
  // If availableCourses is empty, return userCourses directly to avoid filtering everything out
  // This prevents the menu from being empty if opened before availableCourses has loaded
  if (availableCourses.length === 0) {
    return objects as unknown as MenuEducation[];
  }

  // Create a map of user courses for quick lookup
  const userCoursesMap = new Map<string, any>();
  objects.forEach((userCourse: any) => {
    userCoursesMap.set(userCourse.ID, userCourse);
  });

  // Merge available course metadata with user-specific data (like highestLevelCompleted)
  const matchedCourses = availableCourses
    .filter((course) => userCoursesMap.has(course.ID))
    .map((course) => {
      const userCourseData = userCoursesMap.get(course.ID);
      const courseAny = course as any;
      // Merge: course metadata + user-specific progress data
      return {
        ...course,
        ...userCourseData,
        // Ensure course metadata is preserved (in case userCourseData has same keys)
        Title: courseAny.Title,
        PublishedStatus: courseAny.PublishedStatus ?? course.PublishedStatus,
      };
    });

  // If we have user courses but no matches, it means availableCourses hasn't fully loaded yet
  // Return user courses directly as a fallback to prevent empty menu (race condition protection)
  if (matchedCourses.length === 0 && objects.length > 0) {
    return objects as unknown as MenuEducation[];
  }

  return matchedCourses as MenuEducation[];
};
